import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Table from 'components/Table/detailsTable';
import mockDetails from 'components/Table/mockdata/details.json';
import devices from 'components/Table/mockdata/devices.json';
import SearchBar from 'components/SearchBar';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { normalizeText } from 'utils/formatting';
import Detail from 'typings/Detail';
import SelectFilter from 'components/SelectFilter';
import { Container, Detaild, Filters, H1, H2, H3, Input } from './styles';

const Details: React.FC = () => {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState([] as { value: string; type: string; }[]);
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
    },
    out: {
      opacity: 0,
    },
  };
  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5,
  };

  const filteredData = useCallback(() => {

    let details: Detail[] = mockDetails;
    const collection = document.getElementsByClassName("summary-title");

    if (searchValue) {
      const normalizedSearch = normalizeText(searchValue);
      const deviceFields = ['device', 'owner', 'status'];
      details = mockDetails.filter((device: Record<string, string>) =>
        deviceFields.some(field =>
          normalizeText(device[field]).includes(normalizedSearch),
        ),
      );
    }

    if (filterValues.length) {
      details = details.filter(d => {
        return filterValues.some(({ type, value }) => {
          return d[type] === value
        });
      });
    }

    if (filterStart) {
      const start = filterStart;
      const deviceFields = ['expireDate'];
      details = mockDetails.filter((device: Record<string, string>) =>
        deviceFields.every(field =>
          device[field] >= start,
        ),
      );
      collection[0].innerHTML = `${filterStart}~yyyymmdd`;
    }

    if (filterEnd) {
      const end = filterEnd;
      const deviceFields = ['expireDate'];
      details = mockDetails.filter((device: Record<string, string>) =>
        deviceFields.every(field =>
          device[field] <= end,
        ),
      );
      collection[0].innerHTML = `yyyymmdd~${filterEnd}`;
    }

    if (filterStart && filterEnd) {
      const start = filterStart;
      const end = filterEnd;
      const deviceFields = ['expireDate'];
      details = mockDetails.filter((device: Record<string, string>) =>
        deviceFields.every(field =>
          device[field] >= start && device[field] <= end
        ),
      );
      collection[0].innerHTML = `${filterStart}~${filterEnd}`;
    }

    return details;

  }, [searchValue, filterValues, filterStart, filterEnd]);

  const options = [
    {
      type: "type",
      options: [
        { value: 'encoder', label: 'Encoder', type: "type" },
        { value: 'decoder', label: 'Decoder', type: "type" },
        { value: 'mux', label: 'Mux', type: "type" },
      ]
    }
  ];

  const state = [
    { value: '0', label: `${t('Inventory')}` },
    { value: '1', label: `${t('Demo')}` },
    { value: '2', label: `${t('Rented')}` },
    { value: '3', label: `${t('Installment')}` },
    { value: '4', label: `${t('Sold')}` },
  ];

  const history = useHistory<{ serial: string }>();
  const location = useLocation();

  const getLastItem = (thePath: string) =>
    thePath.substring(thePath.lastIndexOf('/') + 1);

  const serial = getLastItem(location.pathname);

  const device = devices.find(d => d.serialNumber === serial);

  const getStatusColor = useCallback(() => {
    if (!device) {
      return '';
    }
    const { status } = device;
    if (status === '4') {
      return 'var(--color-system-green)';
    }
    if (status === '0') {
      return 'var(--color-system-red)';
    }
    return 'var(--color-system-yellow)';
  }, [device]);

  const data = new Date();
  const d = data.getDate().toString();
  const dF = (d.length === 1) ? `0${d}` : d;
  const m = data.getMonth() + 1;
  const mF = (((m).toString()).length === 1) ? `0${m.toString()}` : m.toString();
  const mE = (((m + 1).toString()).length === 1) ? `0${(m + 1).toString()}` : (m + 1).toString();
  const a = data.getFullYear();
  const start = `${a}-${mF}-${dF}`;
  const end = `${a}-${mE}-${dF}`;

  return (
    <Container>
      <div className="Details" />
      <motion.div
        style={{
          display: 'flex',
          flex: '1',
        }}
        initial="initial"
        animate="in"
        exit="out"
        variants={pageVariants}
        transition={pageTransition}
      >
        <div>
          <SearchBar onSearch={setSearchValue} />
          <Filters>
            <SelectFilter placeholder={`${t('LicensesType')}`} options={options} onChange={opts => {
              setFilterValues(opts);
            }} />
            <Detaild>
              <summary>
                <span className="summary-title">{`${t('RangeType')}`}</span>
                <div className="summary-chevron-up">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="feather feather-chevron-down">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </summary>
              <div className="summary-content">
                <Input type="date" id="start" name="start" defaultValue={start} onChange={() => {
                  const This = document.getElementById("start");
                  if (This instanceof HTMLInputElement)
                    setFilterStart(This?.value);
                  if (document.getElementById("start")?.innerHTML === "" && document.getElementById("end")?.innerHTML === "")
                    document.getElementsByClassName("summary-title")[0].innerHTML = `${t('RangeType')}`;
                }} />
                <Input type="date" id="end" name="end" defaultValue={end} onChange={() => {
                  const This = document.getElementById("end");
                  if (This instanceof HTMLInputElement)
                    setFilterEnd(This?.value);
                  if (document.getElementById("start")?.innerHTML === "" && document.getElementById("end")?.innerHTML === "")
                    document.getElementsByClassName("summary-title")[0].innerHTML = `${t('RangeType')}`;
                }} />
              </div>
            </Detaild>
          </Filters>
        </div>
        <H1
          onClick={() => {
            history.push('/dashboard');
          }}
        >
          &#8617;&nbsp;
          {t('Dashboard')}
        </H1>
        <H2>
          &#62;&nbsp; {t('Devices').slice(0, -1)} {serial}
          <H3 color={getStatusColor()}>{state[device?.status ?? ''].label}</H3>
        </H2>
        <div>
          <Table data={filteredData()} />
        </div>
      </motion.div>
    </Container>
  );
};

export default Details;
